'use client';

import { faUsers } from '@fortawesome/pro-light-svg-icons';
import { faHeart as regularHeart } from '@fortawesome/pro-regular-svg-icons';
import { faHeart as solidHeart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

import CustomLink from '../../ui/custom-link';
import { CLUBS_PLACEHOLDER_IMAGE } from '../utils/constants';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';

export interface MyClubShort {
	id: string;
	location: string;
	logo: string;
	memberCount: number;
	slug: string;
	title: string;
	agreedToAllWaivers?: boolean;
	isUserLoggedIn?: boolean;
	isFavourite?: boolean | null;
	showFavoriteIcon?: boolean;
	isDisabled?: boolean;
	handleFavoriteToggle?: (event: React.MouseEvent, id: string, isFavourite?: boolean | null) => void;
	expired?: boolean | null;
	/**
	 * Override the click-through URL. When set, the card wraps itself in a `CustomLink` pointing at this
	 * href. Mirrors `TournamentCard.customCardHref` / `LeagueCard.customCardHref` /
	 * `TeamLeagueCard.customCardHref` so the listing-ad rotator drives all four cards through the same
	 * prop. By default `MyClubCard` does not link itself — when this prop is absent, behavior is unchanged.
	 */
	customCardHref?: string;
	/**
	 * When paired with `customCardHref`, opens the click-through in a new tab (`target="_blank"` + safe
	 * `rel="noopener noreferrer"`). No effect when `customCardHref` is not set.
	 */
	openInNewTab?: boolean;
	/**
	 * Render the click-through as a plain `<a>` (full document navigation) instead of a Next `<Link>`.
	 * Set by the listing-ad rotator: an ad's URL carries Everflow parameters that the landing middleware
	 * has to see, and Next would route a same-origin URL client-side and skip it.
	 */
	nativeAnchor?: boolean;
}

export const MyClubCard = ({
	id,
	logo,
	location,
	memberCount,
	title,
	isUserLoggedIn,
	isFavourite,
	showFavoriteIcon,
	isDisabled,
	handleFavoriteToggle,
	expired,
	customCardHref,
	openInNewTab,
	nativeAnchor
}: MyClubShort) => {
	const [isPlaceholderImage, setIsPlaceholderImage] = useState(false);

	const getImageToDisplay = useMemo(() => {
		if (logo) {
			if (logo === CLUBS_PLACEHOLDER_IMAGE) setIsPlaceholderImage(true);
			return getCDNURL(logo, 566, 384);
		}
		setIsPlaceholderImage(true);
		return getCDNURL(CLUBS_PLACEHOLDER_IMAGE, 566, 384);
	}, [logo]);

	// `customCardHref` opt-in wrapping — when set, the card becomes a clickable anchor pointing at the
	// provided URL (mirrors the same prop on `TournamentCard` / `LeagueCard` / `TeamLeagueCard`). When unset,
	// behavior is unchanged: the card renders as a plain non-linked div, same as before this prop existed.
	return customCardHref ? (
		<CustomLink
			href={customCardHref}
			className="block h-full w-full"
			target={openInNewTab ? '_blank' : undefined}
			rel={openInNewTab ? 'noopener noreferrer' : undefined}
			native={nativeAnchor}
		>
			<MyClubCardInner
				expired={expired}
				getImageToDisplay={getImageToDisplay}
				title={title}
				isPlaceholderImage={isPlaceholderImage}
				showFavoriteIcon={showFavoriteIcon}
				isUserLoggedIn={isUserLoggedIn}
				isDisabled={isDisabled}
				isFavourite={isFavourite}
				id={id}
				handleFavoriteToggle={handleFavoriteToggle}
				memberCount={memberCount}
				location={location}
			/>
		</CustomLink>
	) : (
		<MyClubCardInner
			expired={expired}
			getImageToDisplay={getImageToDisplay}
			title={title}
			isPlaceholderImage={isPlaceholderImage}
			showFavoriteIcon={showFavoriteIcon}
			isUserLoggedIn={isUserLoggedIn}
			isDisabled={isDisabled}
			isFavourite={isFavourite}
			id={id}
			handleFavoriteToggle={handleFavoriteToggle}
			memberCount={memberCount}
			location={location}
		/>
	);
};

/**
 * Card markup extracted to a separate component so the linked / non-linked branches above can reuse it
 * without duplicating ~80 lines of JSX.
 */
interface MyClubCardInnerProps {
	expired?: boolean | null;
	getImageToDisplay: string;
	title: string;
	isPlaceholderImage: boolean;
	showFavoriteIcon?: boolean;
	isUserLoggedIn?: boolean;
	isDisabled?: boolean;
	isFavourite?: boolean | null;
	id: string;
	handleFavoriteToggle?: (event: React.MouseEvent, id: string, isFavourite?: boolean | null) => void;
	memberCount: number;
	location: string;
}

const MyClubCardInner = ({
	expired,
	getImageToDisplay,
	title,
	isPlaceholderImage,
	showFavoriteIcon,
	isUserLoggedIn,
	isDisabled,
	isFavourite,
	id,
	handleFavoriteToggle,
	memberCount,
	location
}: MyClubCardInnerProps) => {
	return (
		<div className="group relative flex size-full cursor-pointer flex-col min-[500px]:min-w-[224px] min-[500px]:max-w-[286px] overflow-hidden rounded-xl border bg-white">
			{expired ? (
				<div className="absolute top-0 left-0 right-0 z-10 !bg-error-600 text-white text-[10px] sm:text-xs font-semibold text-center py-0.5 sm:py-1 px-1 sm:px-2">
					Expired Membership
				</div>
			) : null}
			<div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
				<img
					src={getImageToDisplay}
					alt={title}
					className={clsx('absolute top-0 left-0 h-full w-full object-contain', isPlaceholderImage && 'py-4')}
				/>

				{!!showFavoriteIcon && !!isUserLoggedIn && (
					<button
						onClick={(event) => handleFavoriteToggle?.(event, id, isFavourite)}
						className={clsx(
							`absolute z-20 right-2 top-2 transition-transform duration-150 flex h-[34px] w-[34px] items-center justify-center rounded-md border border-gray-300 bg-white shadow-md`,
							{
								'cursor-not-allowed opacity-80': isDisabled,
								'cursor-pointer': !isDisabled
							}
						)}
						disabled={isDisabled}
					>
						<div className=" bg-white">
							<FontAwesomeIcon
								icon={isFavourite ? solidHeart : regularHeart}
								size="lg"
								className={`text-md transition-colors duration-150 ${isFavourite ? 'text-error-700' : 'text-gray-600'}`}
							/>
						</div>
					</button>
				)}
			</div>
			<div className="group-hover:bg-gray-50 flex flex-1 flex-col gap-2 p-3 sm:p-4 sm:pt-3">
				<div
					className="block text-xs font-semibold uppercase leading-[18px] text-gray-900 sm:line-clamp-2 sm:text-sm sm:font-medium sm:leading-5"
					style={{ minHeight: '2rem' }}
					title={title}
				>
					{title}
				</div>
				<div className="flex flex-wrap text-sm font-normal text-gray-600 leading-5 flex-row">
					<div className="flex flex-wrap text-sm font-normal text-gray-600 leading-5 flex-row">
						<div className="relative group/member">
							<div className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-full cursor-pointer">
								<FontAwesomeIcon icon={faUsers} />
								{memberCount || 0}
							</div>
							<span
								className="absolute left-full -top-9 px-3 py-2 -translate-x-1/2 opacity-0 group-hover/member:opacity-100 transition-opacity duration-200 bg-gray-900 text-white text-sm font-normal rounded-md whitespace-nowrap
		before:content-[''] before:absolute before:left-1/4 before:top-full before:-translate-x-1/2 before:border-4 before:border-transparent before:border-t-gray-900"
							>
								Members
							</span>
						</div>
					</div>
				</div>
				{location ? (
					<div className="text-sm font-normal line-clamp-1 text-gray-600 leading-5" style={{ minHeight: '1.25rem' }}>
						{location}
					</div>
				) : null}
			</div>
		</div>
	);
};
